import { Set } from 'immutable';
import { createSelector } from 'reselect';
import { getFinish, getWood, getDistress, getDefaultProps, getOrderedFieldsArray, getCustomOptions, selectedFieldOption, getWoodThumbnail, getDistressThumbnail, getDefaultPropsByFieldName, addSuffixForValidFieldValue } from './index';
import * as fieldName from '../constants/Fields';
import * as flooringFieldName from '../constants/FlooringFields';

const getWidth = (state) => state.getIn(['fields', flooringFieldName.WIDTH]);
const getQuantity = (state) => state.getIn(['fields', flooringFieldName.QUANTITY]);
const getEdgeDetail = (state) => state.getIn(['fields', flooringFieldName.EDGE_DETAIL]);
const getSheen = (state) => state.getIn(['fields', flooringFieldName.SHEEN]);

const getWidthOptions = createSelector(
  [getWidth, getWood],
  (width, wood) => {
    const woodValue = wood.get('value');
    

    const calculatedProps = {
      summaryText: addSuffixForValidFieldValue(width, ' wide')
    };

    if (woodValue) {
      const noNineInchWidths = ['Barnwood', 'Cherry', 'Oak (Red)'];
      const isExcludedNineInchWood = noNineInchWidths.filter( woodToken => woodValue.indexOf(woodToken) >= 0 ).length > 0;

      if (isExcludedNineInchWood) {
        const options = width.get('options');
        calculatedProps.disabled = options.filter(option => option.value.indexOf('9"') >= 0).map(option => option.displayName);
      }
    }

    return Object.assign({}, getDefaultProps(width), calculatedProps);
  }
);

const getEdgeDetailOptions = createSelector(
  [getEdgeDetail],
  (edgeDetail) => {
    const calculatedProps = {
      summaryText: addSuffixForValidFieldValue(edgeDetail, ' edge detail')
    };

    return Object.assign({}, getDefaultProps(edgeDetail), calculatedProps);
  }
);

const getQuantityOptions = createSelector(
  [getQuantity],
  (quantity) => {
    const calculatedProps = {
      note: quantity.get('note'),
      summaryText: addSuffixForValidFieldValue(quantity, ' square feet')
    };

    return Object.assign({}, getDefaultProps(quantity), calculatedProps);
  }
);

const getDistressOptions = createSelector(
  [getDistress, getWood],
  (distress, wood) => {
    const woodValue = wood.get('value');
    const reclaimedWoods = ['Reclaimed Barnwood (Weathered Face)', 'Reclaimed Barnwood (Smooth Face)'];
    const isReclaimedWood = reclaimedWoods.indexOf(woodValue) >= 0;
    let calculatedProps = {};
    
    if (isReclaimedWood) {
      calculatedProps.hide = true;
    }
    
    return Object.assign({}, getDefaultProps(distress), calculatedProps);
  }
);

const getFinishOptions = createSelector(
  [getFinish, getWood, getEdgeDetail],
  (finish, wood, edgeDetail) => {
    let calculatedProps = { summaryText: addSuffixForValidFieldValue(finish, ' wood finish') };
    let species;
    const woodValue = wood.get('value');
    const edgeDetailValue = edgeDetail.get('value');
    const unfinishedEdgeDetail = 'Square Edge (Unfinished Only)';
    
    if (woodValue) {
      var selectedWood = wood.get('options').filter( woodOption => woodOption.value === woodValue );
      species = selectedWood[0] ? selectedWood[0].species : false;
    }
    
    if (species && species !== 'N/A') {
      const options = finish.get('options').filter( (option) => option.species === species );
      calculatedProps.options = options;
      
      if (edgeDetailValue === unfinishedEdgeDetail) {
        calculatedProps.disabled = options.filter( (option) => option.value.indexOf('None ') < 0 ).map( (option) => option.value );
      }
    } else {
      const unavailableText = (species === 'N/A') ? species : 'Please select Wood Species/Grade/Cut (above)';
    
      calculatedProps = {
        options: [
          {
            id: 'unavailable',
            value: unavailableText,
            displayName: unavailableText
          }
        ],
        blank: false,
        blank_label: false,
        options_modal: false,
        disabled: [unavailableText],
        summaryText: null
      }
    }
    
    return Object.assign({}, getDefaultProps(finish), calculatedProps);
  }
);

const getSheenOptions = createSelector(
  [getSheen, getFinish],
  (sheen, finish) => {
    const finishValue = finish.get('value');
    let calculatedProps = {
      summaryText: addSuffixForValidFieldValue(sheen, ' sheen')
    };
    
    if (finishValue) {
      const finishValueLowerCase = finishValue.toLowerCase();
      const finishesToHideSheen = ['not sure', 'none'];
      const hideSheen = finishesToHideSheen.reduce( (hide, finishOption) => hide || finishValueLowerCase.indexOf(finishOption) > -1, false );
      calculatedProps.hide = hideSheen;
    }

    return Object.assign({}, getDefaultProps(sheen), calculatedProps);
  }
);

const getEdgeDetailThumbnail = createSelector(getEdgeDetail, edgeDetail => Object.assign({}, selectedFieldOption(edgeDetail), {label: 'Edge Detail', class: 'edge-detail'}));

export const getFlooringFieldProps = (state, name) => {
  switch (name) {
    case flooringFieldName.WIDTH:
      return getWidthOptions(state);
    case flooringFieldName.EDGE_DETAIL:
      return getEdgeDetailOptions(state);
    case flooringFieldName.QUANTITY:
      return getQuantityOptions(state);
    case flooringFieldName.SHEEN:
      return getSheenOptions(state);
    case fieldName.DISTRESS:
      return getDistressOptions(state);
    case fieldName.FINISH:
      return getFinishOptions(state);
    case fieldName.CUSTOM:
      return getCustomOptions(state);
  }

  return getDefaultPropsByFieldName(state, name);
};

export const getFlooringSelectedOption = (state, name) => {
  switch(name) {
    case flooringFieldName.EDGE_DETAIL:
      return getEdgeDetailThumbnail(state);
    case fieldName.WOOD:
      return getWoodThumbnail(state);
    case fieldName.DISTRESS:
      return getDistressThumbnail(state);
  }
};

export const getFlooringSummaryFieldValues = (state) => {
  return getOrderedFieldsArray(state).map((name) => {
    const props = getFlooringFieldProps(state, name);
    return (props.hide || props.disabled.indexOf(props.selected) >= 0) ? false : props.summaryText;
  }).filter((displayValue) => displayValue ? true : false);
};

export const getFlooringOptionThumbnails = (state) => {
  const thumbnailFields = [fieldName.WOOD, fieldName.DISTRESS, flooringFieldName.EDGE_DETAIL];
  return thumbnailFields.map((name) => {
    const fieldProps = getFlooringFieldProps(state, name),
          disabledOptions = fieldProps.disabled,
          isDisabled = disabledOptions && disabledOptions.indexOf(fieldProps.selected) > -1,
          isHidden = fieldProps.hide === true;

    return (isDisabled || isHidden) ? {} : getFlooringSelectedOption(state, name);
  }).filter(thumbnail => thumbnail.image ? true : false);
};
